import React from 'react'
import moment from 'moment'
import { Message } from '@paypalcorp/worldready-react'
import _ from 'lodash'

import { shareConstants } from './constants/shareConstants'
import { constants } from './constants/constants'
import { fileOptionsConstants } from './constants/fileOptions'
import serverData from './utils/server-data'

export function stringToBoolean(value) {
  return typeof value === 'string' && value.toLowerCase() === 'true'
}

export function booleantoString(value) {
  return value ? 'true' : 'false'
}

export function stringToInteger(value) {
  /* 10 as radix parameter */
  return parseInt(value, 10)
}

export function formatFileSize(bytes, decimalPoint = 2) {
  if (bytes === 0) return '0 Bytes'
  const k = 1000,
    sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k))
  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(decimalPoint)) + ' ' + sizes[i]
  )
}
export function dateConverter(date, format = 'MMM DD, YYYY hh:mm A') {
  return moment(date).format(format)
}

export function folderTreeStructure(folders) {
  const hierarchy = {
    path: '/',
  }
  folders.forEach(function (folder) {
    removeExtraSlashes(folder.path)
      .split('/')
      .reduce(function (currentFolder, item) {
        const currentPath = currentFolder.path + item + '/'
        currentFolder[item] = currentFolder[item] || {}
        currentFolder = currentFolder[item]
        currentFolder.path = currentPath
        currentFolder.enableEmailMask = updateEmailMaskStatus(
          folders,
          currentPath,
        )
        return currentFolder
      }, hierarchy)
  }, {})
  return hierarchy
}

export function updateEmailMaskStatus(folders, path) {
  if (_.endsWith(path, '/')) {
    path = path.slice(0, -1)
  }
  const filteredFolder = _.filter(folders, function (folder) {
    return folder.path === path
  })
  return _.has(filteredFolder[0], 'enableEmailMask')
    ? filteredFolder[0].enableEmailMask
    : false
}

export function checkPermissions(source, destination, item, permissions) {
  return Object.keys(permissions).every(
    (key) => source[item][key] === destination[item][key],
  )
}

export function setPermissionForAll(shareeList, permission) {
  return Object.keys(shareeList).reduce(function (sharee, keys) {
    Object.keys(permission).forEach(function (key) {
      sharee[keys][key] = permission[key]
    })
    return sharee
  }, shareeList)
}

export function copyAndCreateObject(source, keys) {
  return keys.reduce(function (result, key) {
    result[key] = source[key]
    return result
  }, {})
}

export function checkUserPermissionForSharedFolder(item, user, userPermission) {
  /* Set permissions for user, based on userType and shared status of folder */
  if (_.has(item, 'sharedBy')) {
    return calculateUserPermission(item, userPermission)
  }
  if (isCorpOrGoPayUser(user)) {
    return shareConstants.FilePermissions.readWriteExecute
  }
  return userPermission
}

export function calculateUserPermission(item, userPermission) {
  let permission = 0
  if (item.permission) {
    permission =
      shareConstants.FilePermissions[
        shareConstants.FilePermissionFromServer[item.permission]
      ]
  } else {
    permission = userPermission
  }
  return permission
}

export function validateFolderName(folderName, fileType) {
  let error = ''

  const checkSpecialChar = /[#%\[\]:\/\\?]/

  /* Punctuation mark(:) is not allowed by file system.
     The characters # % / \ ? [ ] are browser unsafe characters
  */
  const containSpecialChar = checkSpecialChar.test(folderName)
  const isHiddenFileOrFolder = folderName.startsWith('.')

  if (isHiddenFileOrFolder) {
    error = getLocalizedContent(`alert.dot.not.allowed.${fileType}`)
  } else if (containSpecialChar) {
    error = getLocalizedContent('alert.special.chars.not.allowed', folderName)
  }
  return error
}

export function sortFiles(list, sortBy, sortType) {
  const sortHelpers = [
    ['fileName', (file) => file.fileName.toLowerCase()],
    ['fileType', (file) => file.fileType.toLowerCase()],
    ['size', (file) => file.size],
    ['modifiedDate', (file) => file.modifiedDate.toLowerCase()],
    ['optype', (file) => file.optype],
    ['user', (file) => file.user],
    ['timestamp', (file) => file.timestamp],
    ['status', (file) => file.status],
  ]
  const sortHelpersMap = new Map(sortHelpers)

  return _.orderBy(list, sortHelpersMap.get(sortBy), sortType)
}

export function isUserHasAccess(user, accessFor, deployEnv = {}) {
  const isCOC = isCOCUser(user)

  if (
    user.accType === constants.AdhocUser &&
    accessFor === constants.Users.user
  ) {
    return { hasAccess: true, isCOC }
  }

  if (
    isCorpOrGoPayUser(user) &&
    accessFor === constants.Users.user &&
    _.get(deployEnv, 'isQA', false)
  ) {
    return { hasAccess: true, isCOC }
  }

  if (isCorpOrGoPayUser(user) && user.hasOwnProperty('Role')) {
    return { hasAccess: checkCorpUserAccess(user, accessFor, isCOC), isCOC }
  }
  return { hasAccess: false, isCOC }
}

export function isCOCUser(user) {
  const userCountry = (user?.countrycode?.toUpperCase() || '').substring(0, 3)
  return shareConstants.COCCountries.includes(userCountry)
}

export function checkCorpUserAccess(user, accessFor, isCOC) {
  switch (accessFor) {
    case 'User':
      // COC users must have PP_SSO_DZ_COC_USER, non-COC users must have PP_SSO_DZ_USER
      if (isCOC) {
        return user.Role.includes(constants.IAMRole.cocUser)
      }
      return user.Role.includes(constants.IAMRole.user)
    case 'Share':
      // COC users must have PP_SSO_DZ_COC_SHARER, non-COC users must have PP_SSO_DZ_SHARER
      if (isCOC) {
        return user.Role.includes(constants.IAMRole.cocSharer)
      }
      return user.Role.includes(constants.IAMRole.sharer)
    case 'Content':
      return user.Role.includes(constants.IAMRole.msgSender)
    default:
      return false
  }
}

export function removeExtraSlashes(string) {
  return string.replace(/^\/+|\/+$/g, '')
}

export function removeTrailingSlashes(string) {
  return string.replace(/([^:]\/)\/+/g, '$1')
}

export function checkNoDataMessageVisibility(
  user,
  baseFolder,
  userPermission,
  type,
) {
  if (user.accType === constants.AdhocUser && baseFolder === '/') {
    return false
  } else if (
    userPermission === shareConstants.FilePermissions.list ||
    userPermission === shareConstants.FilePermissions.read
  ) {
    return false
  } else if (
    type === 'createFolder' &&
    userPermission === shareConstants.FilePermissions.readWrite
  ) {
    return false
  }
  return true
}

export function constructUserNameToDisplay(userId) {
  if (userId.search('@') === -1) {
    return `${userId}@paypal.com`
  }
  return userId
}

export function constructUploadFileErrorMessage(
  notProperFiles,
  selectedDotFiles,
  selectedFolders,
) {
  if (notProperFiles.length > 0) {
    const errorMessage = _.reduce(
      notProperFiles,
      function (message, file) {
        return `${message} ${file.name},`
      },
      '',
    )
    return getLocalizedContent('alert.special.chars.not.allowed', errorMessage)
  }

  if (selectedDotFiles.length > 0) {
    return getLocalizedContent('alert.dot.not.allowed.file')
  }
  if (selectedFolders.length > 0) {
    return getLocalizedContent('error.upload.only.files')
  }
  return ''
}

export function isCorpOrGoPayUser(user) {
  return (
    user.accType === constants.CorpUser || user.accType === constants.GoPayUser
  )
}

export function capitalize([first, ...rest]) {
  return first.toUpperCase() + rest.join('').toLowerCase()
}

export function getKeyByValue(arr, value) {
  return arr.find((o) => o.name === value)
}

export function getLocalizedContent(
  id,
  filename = '',
  fileType = '',
  optype = '',
) {
  if (filename !== '' && fileType !== '' && optype !== '') {
    return (
      <Message
        id={id}
        filename={filename}
        fileType={fileType}
        optype={optype}
      />
    )
  }
  if (filename !== '' && fileType !== '') {
    return <Message id={id} filename={filename} fileType={fileType} />
  }
  if (filename !== '') {
    return <Message id={id} filename={filename} />
  }
  if (fileType !== '') {
    return <Message id={id} fileType={fileType} />
  }
  return <Message id={id} />
}

export function isFile(type) {
  return type === fileOptionsConstants.FileTypes.file
}

export function checkEmailMaskBeforeRender(
  list = {},
  key,
  folderList = {},
  isTreeView = false,
) {
  const user = serverData.user
  if (isTreeView) {
    const emailMask = folderList[key].enableEmailMask
    key = key.endsWith('/') ? key.slice(0, -1) : key
    if (emailMask) {
      return key.replace(
        /(([^.@\s]+)(\.[^.@\s]+)*@([^.@\s]+\.)+([^.@\s]+))$/,
        '',
      )
    }
    return key
  } else if (_.has(list, 'sharedBy') && _.has(list, 'enableEmailMask')) {
    if (list.enableEmailMask && user.accType === constants.AdhocUser) {
      if (_.has(list, key)) {
        let value = list[key]
        if (key === 'path') {
          let splittedValue = value.split('/')
          splittedValue[1] = splittedValue[1].replace(
            /(([^.@\s]+)(\.[^.@\s]+)*@([^.@\s]+\.)+([^.@\s]+))$/,
            '',
          )
          value = splittedValue.join('/')
          return value
        }
        return list[key].replace(
          /(([^.@\s]+)(\.[^.@\s]+)*@([^.@\s]+\.)+([^.@\s]+))$/,
          '',
        )
      } else if (list.fileName === key)
        return key.replace(
          /(([^.@\s]+)(\.[^.@\s]+)*@([^.@\s]+\.)+([^.@\s]+))$/,
          '',
        )
    }
  } else if (Object.keys(list).length === 0) {
    return key
  }
  return list[key]
}

export function renderRibbonNotification() {
  return (
    <div className="home__notification">
      <div className="home__notification-content flexCenter">
        <span>Discover the true potential of DropZone - </span>
        <span style={{ paddingLeft: '5px' }}>
          <a
            data-pa-pgrp={`dropzone:paypalcorp`}
            data-pa-page={`dropzone:paypalcorp:home`}
            data-pa-click={'dz-101'}
            href="https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=974859100"
            target="_blank"
            rel="noreferrer"
            id="dz_101"
            className="ppvx_link flexCenter"
            pa-marked="1"
          >
            DropZone 101
          </a>
        </span>
      </div>
    </div>
  )
}

export default {
  stringToBoolean,
  booleantoString,
  stringToInteger,
  formatFileSize,
  dateConverter,
  folderTreeStructure,
  checkPermissions,
  setPermissionForAll,
  copyAndCreateObject,
  checkUserPermissionForSharedFolder,
  calculateUserPermission,
  validateFolderName,
  sortFiles,
  isUserHasAccess,
  isCOCUser,
  removeExtraSlashes,
  checkNoDataMessageVisibility,
  removeTrailingSlashes,
  constructUserNameToDisplay,
  constructUploadFileErrorMessage,
  isCorpOrGoPayUser,
  capitalize,
  getKeyByValue,
  getLocalizedContent,
  isFile,
  checkEmailMaskBeforeRender,
  renderRibbonNotification,
}
